import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug, fetchBordpyntGalleri } from '../lib/cms';
import GalleryLightbox from '../components/GalleryLightbox';

export default function Bordpynt() {
  const [pageData, setPageData] = useState(null);
  const [galleryImages, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      const [page, bordpyntItems] = await Promise.all([
        fetchPageBySlug('ideer-til-bordpynt-og-dekorationer'),
        fetchBordpyntGalleri(),
      ]);
      setPageData(page);

      if (Array.isArray(bordpyntItems) && bordpyntItems.length > 0) {
        const formatted = bordpyntItems
          .map((item) => {
            const imgUrl = Array.isArray(item.billede) ? item.billede[0] : item.billede;
            if (!imgUrl) return null;
            return {
              url: imgUrl,
              name: '',
            };
          })
          .filter(Boolean);
        setImages(formatted);
      }
      setLoading(false);
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-dahlia-500 mb-4" />
          <p className="text-forest-900 font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20">
      <Helmet>
        <title>{pageData?.meta_titel?.da || pageData?.meta_title?.da || 'Ideer til bordpynt og dekorationer | Dahliahjørnet'}</title>
        <meta name="description" content={pageData?.meta_beskrivelse?.da || pageData?.meta_description?.da || 'Blomster til bordpynt og dekorationer​ Find vej'} />
      </Helmet>
      {/* Header Banner */}
      <div className="relative h-[40vh] min-h-[300px] bg-forest-950 flex items-center justify-center text-center px-6">
        <img
          src={pageData?.banner_billede?.[0] || 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/buket-36.png'}
          alt={pageData?.titel?.da || 'Bordpynt og dekorationer'}
          className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-950 via-forest-950/50 to-transparent" />
        <div className="relative z-10 max-w-4xl">
          <span className="text-white uppercase tracking-[0.25em] font-bold text-xs sm:text-sm mb-3 block">
            {pageData?.underoverskrift?.da || 'Kreativ inspiration'}
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-white leading-tight">
            {pageData?.titel?.da || 'Blomster til bordpynt og dekorationer​'}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Text Content */}
          <div className="lg:col-span-7 flex flex-col gap-8">
            <div className="prose prose-forest max-w-none text-charcoal-700 leading-relaxed flex flex-col gap-6">
              <p className="text-lg font-medium text-forest-950">
                Planlægger du en fest, et sommerbryllup, en rund fødselsdag eller en anden særlig begivenhed? Dahliaer er det perfekte valg til bordpynt og dekorationer. Med deres utrolige farverigdom, forskellige former og imponerende størrelser kan du skabe præcis den stemning, du ønsker – fra det romantiske og klassiske til det sprudlende og moderne.
              </p>
              <div className="bg-cream-100/70 border-l-4 border-[#9b4d85] p-4 rounded-r-xl">
                <p className="text-sm font-medium text-forest-950 m-0">
                  <i className="fa-solid fa-circle-info text-[#9b4d85] mr-2" />
                  <strong>Bemærk om sæsonen:</strong> Dahliaer blomstrer fra midten af juli og frem til den første nattefrost. Blomsterne er derfor <em>ikke</em> klar til konfirmationstiden om foråret, men er helt fantastiske til sensommerens og efterårets arrangementer.
                </p>
              </div>
              <p>
                Du er altid velkommen til at besøge vores pluk-selv mark for at hente inspiration og plukke de helt rigtige blomster til din fest. Vi står klar med rådgivning om, hvilke sorter der egner sig bedst til dekorationer, og hvordan du holder dem friske i længst mulig tid.
              </p>
            </div>

            {/* Video Showcase */}
            <div className="bg-white rounded-3xl p-4 shadow-md border border-cream-200 overflow-hidden">
              <div className="relative aspect-[9/16] sm:aspect-video lg:aspect-[16/9] max-h-[520px] w-full mx-auto rounded-2xl overflow-hidden bg-forest-950">
                <video
                  src="/_assets/mojo-video-1-1.mp4"
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                >
                  Din browser understøtter ikke videoafspilning.
                </video>
              </div>
            </div>

            {/* Gallery Section */}
            {galleryImages.length > 0 && (
              <div className="mt-8 flex flex-col gap-6">
                <h3 className="text-2xl font-serif font-bold text-forest-950">Inspiration til bordpynt &amp; dekorationer</h3>
                <p className="text-sm text-charcoal-600">Klik på et billede for at åbne det i stor visning og swipe igennem.</p>
                <GalleryLightbox images={galleryImages} showTitles={false} />
              </div>
            )}
          </div>

          {/* Sidebar / Info */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            {/* Highlights Box */}
            <div className="bg-white text-charcoal-800 p-8 rounded-3xl shadow-md border border-cream-200">
              <h3 className="text-xl font-serif font-bold text-forest-950 mb-6">Hvad kan du finde hos os?</h3>
              <ul className="flex flex-col gap-4 text-sm text-charcoal-700">
                <li className="flex items-start gap-3">
                  <span className="text-black mt-1.5 flex-shrink-0 text-[8px]">
                    <i className="fa-solid fa-circle" />
                  </span>
                  <span>Stort udvalg af pluk selv dahliablomster</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-black mt-1.5 flex-shrink-0 text-[8px]">
                    <i className="fa-solid fa-circle" />
                  </span>
                  <span>Et utal af farveringe blomster i forskellige størrelse</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-black mt-1.5 flex-shrink-0 text-[8px]">
                    <i className="fa-solid fa-circle" />
                  </span>
                  <span>Køb knolde og stiklinger til din egen have</span>
                </li>
              </ul>
            </div>

            {/* Image Box */}
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=600,fit=cover,quality=85,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/buket-36.png"
              alt="Smuk dahlia buket"
              className="w-full aspect-square object-cover rounded-3xl"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
